import { useState, useCallback } from "react";
import { toast } from "sonner";
import { getCoupon } from "pages/GenericOrder/apis";
import { Coupon } from "../tabs/AmountCharges/Types/discount";

export const useDiscountValidation = () => {
  const [loading, setLoading] = useState(false);

  const getAuthToken = useCallback(() => {
    const auth = localStorage.getItem("authToken");
    return auth ? JSON.parse(auth) : null;
  }, []);

  const validateCoupon = useCallback(async (couponName: string): Promise<Coupon | null> => {
    const token = getAuthToken();
    if (!token) {
      toast.error("Authentication required");
      return null;
    }

    setLoading(true);
    
    try {
      const payload = {
        headers: { Authorization: `Bearer ${token}` },
        params: { coupon_name: couponName },
      };

      const res = await getCoupon(payload);
      const couponData :any= res.data;

      // Validation checks
      const validationErrors = [
        { condition: !couponData?.id, message: "Invalid coupon code" },
        { condition: couponData?.redemption_limit === 0, message: "Coupon usage limit reached" },
        { condition: !couponData?.end_date, message: "Invalid coupon expiration" },
        { 
          condition: couponData?.end_date && new Date(couponData.end_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0), 
          message: "Coupon has expired" 
        }
      ];

      for (const { condition, message } of validationErrors) {
        if (condition) {
          toast.error(message);
          return null;
        }
      }

      return couponData;
    } catch (err: any) {
      const message = err?.response?.data?.payload || 
                    err?.response?.data?.message || 
                    "Failed to validate coupon";
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  const validateDiscountAmount = useCallback((amount: number, orderTotal: number) => {
    const validationRules = [
      { condition: amount <= 0, message: "Discount amount must be positive" },
      { condition: amount > orderTotal, message: "Discount amount can not exceed order total" }
    ];

    for (const { condition, message } of validationRules) {
      if (condition) {
        toast.error(message);
        return false;
      }
    }
    return true;
  }, []);

  return { validateCoupon, validateDiscountAmount, loading };
};